import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { STATUS_LABEL, STATUS_BADGE, RISK_BADGE, LOAN_PRODUCTS } from "@/lib/loanProducts";
import { ArrowLeft, CheckCircle2, XCircle, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";

const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const productTitle = (key) => LOAN_PRODUCTS.find((p) => p.key === key)?.title || key;

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const [app, setApp] = useState(null);
  const [rate, setRate] = useState(18);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/applications/${id}`).then(({ data }) => {
    setApp(data);
    setRate(data.interest_rate_pct || 18);
    setNotes(data.admin_notes || "");
  });

  useEffect(() => { load(); }, [id]);

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/applications/${id}/status`, {
        status,
        interest_rate_pct: status === "approved" ? Number(rate) : null,
        admin_notes: notes || null,
      });
      toast.success(`Application ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!app) return <DashboardLayout role="admin"><div>Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout role="admin">
      <Link to="/admin/applications" className="mb-4 inline-flex items-center gap-2 text-sm text-ink-soft hover:text-[#5B2C82]" data-testid="app-back">
        <ArrowLeft className="h-4 w-4" /> All applications
      </Link>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-xl border border-[#E5E2DE] bg-white p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-heading text-2xl font-semibold text-ink">{app.applicant_name}</h1>
                <div className="text-xs text-ink-muted">{app.applicant_email} · {app.applicant_phone || "—"}</div>
              </div>
              <span className={`rounded-full border px-3 py-1 text-xs ${STATUS_BADGE[app.status]}`}>{STATUS_LABEL[app.status]}</span>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Stat label="Product" value={productTitle(app.product)} />
              <Stat label="Amount" value={fmt(app.amount)} />
              <Stat label="Term" value={`${app.term_months} mo`} />
              <Stat label="Customer type" value={app.customer_type.replace(/_/g, " ")} />
            </div>
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider text-ink-muted">Purpose</div>
              <p className="mt-1 text-sm text-ink-soft">{app.purpose}</p>
            </div>
            {app.credit_check_consent && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs text-emerald-800" data-testid="app-consent-badge">
                ✓ Credit-reference consent given {app.consent_given_at ? `· ${new Date(app.consent_given_at).toLocaleString()}` : ""}
              </div>
            )}
            {app.whatsapp_consent && (
              <div className="ml-2 mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs text-emerald-800" data-testid="app-whatsapp-badge">
                ✓ WhatsApp opt-in
              </div>
            )}
          </div>

          <div className="rounded-xl border border-[#E5E2DE] bg-white p-6">
            <h2 className="font-heading text-lg font-semibold text-ink">Personal & employment</h2>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
              <Field label="National ID" value={app.national_id} />
              <Field label="Date of birth" value={app.date_of_birth} />
              <Field label="Address" value={app.address} />
              <Field label="Employer" value={app.employer_name} />
              <Field label="SSB / Employee #" value={app.employee_number} />
              <Field label="Department" value={app.ministry_department} />
              <Field label="Job title" value={app.job_title} />
              <Field label="Manager" value={app.manager_name} />
              <Field label="Head of Department" value={app.head_of_department} />
              <Field label="Tenure (yrs)" value={app.years_employed} />
              <Field label="Monthly income" value={fmt(app.monthly_income)} />
              <Field label="Business name" value={app.business_name} />
              <Field label="Sector" value={app.business_sector} />
              <Field label="Business revenue" value={app.business_monthly_revenue ? fmt(app.business_monthly_revenue) : "—"} />
              <Field label="Next of kin" value={app.next_of_kin_name} />
              <Field label="Kin phone" value={app.next_of_kin_phone} />
              <Field label="Kin relation" value={app.next_of_kin_relationship} />
              <Field label="Bank" value={app.bank_name} />
              <Field label="Account #" value={app.bank_account_number} />
            </div>
          </div>

          {(app.has_offtake_contract || app.collateral_description) && (
            <div className="rounded-xl border border-[#E5E2DE] bg-white p-6">
              <h2 className="font-heading text-lg font-semibold text-ink">Off-take & collateral</h2>
              <div className="mt-4 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                <Field label="Off-take contract" value={app.has_offtake_contract ? "Yes" : "No"} />
                <Field label="Off-take buyer" value={app.offtake_buyer} />
                <Field label="Collateral description" value={app.collateral_description} />
                <Field label="Estimated value" value={app.collateral_value ? fmt(app.collateral_value) : "—"} />
              </div>
            </div>
          )}

          {Array.isArray(app.documents) && app.documents.length > 0 && (
            <div className="rounded-xl border border-[#E5E2DE] bg-white p-6">
              <h2 className="font-heading text-lg font-semibold text-ink">Documents ({app.documents.length})</h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {app.documents.map((d) => {
                  const token = localStorage.getItem("jrc_token");
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${d.file_id}/download${token ? `?auth=${token}` : ""}`;
                  return (
                    <li key={d.file_id} className="flex items-center gap-3 rounded-lg border border-[#E5E2DE] p-3" data-testid={`admin-doc-${d.kind}`}>
                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-[#E5E2DE] bg-[#F9F6F0]">
                        <AuthImage fileId={d.file_id} contentType={d.content_type} className="h-full w-full" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-heading text-sm font-semibold text-ink capitalize">{d.kind.replace(/_/g, " ")}</div>
                        <div className="truncate text-xs text-ink-muted">{d.original_filename}</div>
                        {d.scan_status === "clean" && (
                          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`admin-doc-scan-${d.kind}`}>
                            ✓ Scanned · clean
                          </span>
                        )}
                        {d.scan_status === "suspicious" && (
                          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800" data-testid={`admin-doc-scan-${d.kind}`}>
                            ⚠ Suspicious — review
                          </span>
                        )}
                      </div>
                      <a href={url} target="_blank" rel="noreferrer"
                        className="ml-3 shrink-0 rounded-full border border-[#5B2C82] px-3 py-1 text-xs font-semibold text-[#5B2C82] hover:bg-[#5B2C82] hover:text-white"
                        data-testid={`admin-doc-view-${d.kind}`}>
                        Open
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-[#E5E2DE] bg-white p-6">
            <h2 className="font-heading text-lg font-semibold text-ink">Risk assessment</h2>
            <div className="mt-3 flex items-center gap-3">
              <span className={`rounded-full border px-3 py-1 text-xs ${RISK_BADGE[app.risk_band]}`}>
                {app.risk_band?.toUpperCase()} · score {app.risk_score}
              </span>
            </div>
            <div className="mt-3 text-sm text-ink-soft">
              Indicative monthly: <span className="font-semibold text-ink">{fmt(app.indicative_monthly_payment)}</span>
            </div>
            <ul className="mt-3 space-y-1 text-xs text-ink-soft">
              {(app.risk_factors || []).map((f, i) => <li key={i}>• {f}</li>)}
            </ul>
          </div>

          <div className="rounded-xl border border-[#E5E2DE] bg-white p-6">
            <h2 className="font-heading text-lg font-semibold text-ink">Decision</h2>
            <div className="mt-4 space-y-3">
              <div>
                <Label>Approved interest rate (% p.a.)</Label>
                <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="mt-1.5" data-testid="decision-rate" />
              </div>
              <div>
                <Label>Internal notes</Label>
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" data-testid="decision-notes" />
              </div>
              <div className="space-y-2 pt-2">
                <Button onClick={() => updateStatus("under_review")} disabled={busy} variant="outline" className="w-full" data-testid="decision-review">
                  <ClipboardCheck className="mr-2 h-4 w-4" /> Mark under review
                </Button>
                <Button onClick={() => updateStatus("approved")} disabled={busy} className="w-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="decision-approve">
                  <CheckCircle2 className="mr-2 h-4 w-4" /> Approve & disburse
                </Button>
                <Button onClick={() => updateStatus("rejected")} disabled={busy} className="w-full bg-rose-600 text-white hover:bg-rose-700" data-testid="decision-reject">
                  <XCircle className="mr-2 h-4 w-4" /> Reject
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-0.5 font-heading text-base font-semibold text-ink">{value}</div>
    </div>
  );
}
function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-0.5 text-sm text-ink">{value || "—"}</div>
    </div>
  );
}
